/**
 * Road-closure DATA-COVERAGE honesty (#337) — the display-only truth that the
 * road-closures feed reaches ONLY Ontario, Alberta & British Columbia, so an EMPTY
 * layer elsewhere reads as "no feed for this province", NOT "no closures here".
 *
 * Same honest-degradation class as the #254 feed-down chip and the #325 api-health
 * banner: it states plainly what the map cannot show and NEVER fabricates an
 * all-clear. Here the discipline is unknown-≠-zero applied to COVERAGE — absence of
 * feed must be visibly distinct from a covered province that genuinely has zero
 * closures right now.
 *
 * This module is PURE logic + constants (no operator strings — those live in
 * ./roadClosureCoverageVocabulary so the vocab lock can scan them). It performs ZERO
 * writes and touches NOTHING in apps/ingestion (the #337 hard boundary): the covered
 * set is a web-side MIRROR of the worker's configured sources
 * (apps/ingestion/src/sources/road-closures/types.ts → PROVINCES), PINNED to the
 * worker by roadClosureCoverage.drift.test.ts so it can never silently drift.
 * (Expanding coverage — wiring more provinces' 511 feeds — is a SEPARATE ingestion
 * build, filed as a follow-on gap row; it is not done here.)
 */

/**
 * The provinces the road-closures worker actually feeds — Ontario (511on), Alberta
 * (511ab) and British Columbia (drivebc). MIRROR of the worker's PROVINCES config;
 * the drift-pin test fails CI if the two ever diverge.
 */
export const COVERED_ROAD_CLOSURE_PROVINCES = ['ON', 'AB', 'BC'] as const;
export type CoveredProvince = (typeof COVERED_ROAD_CLOSURE_PROVINCES)[number];

/** The worker's per-province source discriminators — pinned alongside the codes so a
 *  source rename (e.g. drivebc → …) also trips the drift test. */
export const COVERED_ROAD_CLOSURE_SOURCES = ['511on', '511ab', 'drivebc'] as const;

/** Plain-language display name per covered province (used to build the coverage
 *  copy so the operator note can never drift from the covered set). */
export const COVERED_PROVINCE_DISPLAY_NAME: Record<CoveredProvince, string> = {
  ON: 'Ontario',
  AB: 'Alberta',
  BC: 'British Columbia',
};

/** The covered provinces as display names, in config order — the single list the
 *  standing coverage note renders from. */
export const COVERED_PROVINCE_NAMES: readonly string[] = COVERED_ROAD_CLOSURE_PROVINCES.map(
  (code) => COVERED_PROVINCE_DISPLAY_NAME[code],
);

/** True when a two-letter province code is inside the road-closure feed's coverage. */
export function isRoadClosureCovered(province: string | null | undefined): boolean {
  return province != null && (COVERED_ROAD_CLOSURE_PROVINCES as readonly string[]).includes(province);
}

/** A WGS84 lon/lat rectangle — structurally the api BboxQuery. */
export interface LonLatBox {
  west: number;
  south: number;
  east: number;
  north: number;
}

/**
 * GENEROUS *outer* boxes — each OVER-encloses its covered province (verified to fully
 * contain the province's true extent, deliberately bleeding into neighbours). Their
 * SOLE use: a view that overlaps NONE of them is DEFINITELY outside ON/AB/BC, so the
 * "no feed here" cue drawn from that test is airtight. Over-enclosing only makes the
 * cue fire LESS often (a view hugging a covered border stays ambiguous) — it can never
 * mark a covered province "no feed".
 */
const COVERED_OUTER_BOXES: Record<CoveredProvince, LonLatBox> = {
  // BC true extent ≈ lon −139.06…−114.03, lat 48.30…60.00 → fully enclosed.
  BC: { west: -139.5, south: 47.5, east: -113.5, north: 60.5 },
  // AB true extent ≈ lon −120.00…−110.00, lat 48.99…60.00 → fully enclosed.
  AB: { west: -120.5, south: 48.5, east: -109.5, north: 60.5 },
  // ON true extent ≈ lon −95.16…−74.34, lat 41.66…56.86 → fully enclosed.
  ON: { west: -95.5, south: 41.0, east: -73.5, north: 57.5 },
};

/**
 * CONSERVATIVE *core* boxes — each lies STRICTLY INSIDE its covered province, clear of
 * every Canadian inter-provincial border (a core may approach the US border / coasts /
 * Hudson Bay — those aren't covered-vs-uncovered confusions). Their SOLE use: a view
 * FULLY CONTAINED in a core is DEFINITELY entirely inside one covered province, so the
 * affirmative "feed is live" reading drawn from that test is airtight. Ontario's slanted
 * ON/QC border and the BC/AB continental divide cannot be a rectangle edge, so these
 * cores intentionally EXCLUDE the border cities (Toronto/Ottawa/Calgary/Kamloops):
 * those views fall to the always-honest standing note rather than risk a coverage claim
 * over a neighbour. (A covered border view with real closures is still handled — the
 * count>0 branch shows them regardless of geometry.)
 */
const COVERED_CORE_BOXES: Record<CoveredProvince, LonLatBox> = {
  // Interior BC — west of the BC/AB divide (always east of −121 here) and inland of the
  // Pacific coast (≈ −128…−133).
  BC: { west: -127.0, south: 49.0, east: -121.0, north: 57.0 },
  // Interior AB — east of the Rockies divide (≥ ~−116 at lat ≥51.5) and west of SK
  // (−110); south edge 51.5 is far north of the 49°N US border (no US leak).
  AB: { west: -114.0, south: 51.5, east: -111.0, north: 58.0 },
  // Interior north-central ON — east of MB (−95.16), west of the QC border (≥ −79.5),
  // and (critically) NORTH of the Great Lakes / US border: the south edge is held at
  // 49.0°N so the box never dips into Lake Superior's US shore or the Michigan/Wisconsin
  // land the wider band would admit. Covers the Nipigon–Hearst–Kapuskasing interior; the
  // southern-Ontario cities (Toronto/Ottawa/Sudbury) sit in the ambiguous band → standing
  // note, never a wrong coverage claim.
  ON: { west: -90.0, south: 49.0, east: -81.5, north: 50.5 },
};

/** Axis-aligned rectangle overlap (any shared area, edges inclusive). */
function boxesOverlap(a: LonLatBox, b: LonLatBox): boolean {
  return a.west <= b.east && a.east >= b.west && a.south <= b.north && a.north >= b.south;
}

/** True when `outer` fully contains `inner` (inner ⊆ outer, edges inclusive). */
function boxContains(outer: LonLatBox, inner: LonLatBox): boolean {
  return (
    outer.west <= inner.west &&
    outer.east >= inner.east &&
    outer.south <= inner.south &&
    outer.north >= inner.north
  );
}

/**
 * CONFIDENTLY inside coverage: the view is FULLY contained in a conservative core ⇒ it
 * is entirely inside one covered province. Basis for the affirmative "feed is live"
 * reading — never true over an uncovered province.
 */
export function viewConfidentlyCovered(view: LonLatBox | null | undefined): boolean {
  if (!view) return false;
  return (Object.values(COVERED_CORE_BOXES) as LonLatBox[]).some((core) => boxContains(core, view));
}

/**
 * CONFIDENTLY outside coverage: the view overlaps NONE of the generous outer boxes ⇒ it
 * touches no covered-province land. Basis for the "no feed here" cue — never true over a
 * covered province.
 */
export function viewConfidentlyUncovered(view: LonLatBox | null | undefined): boolean {
  if (!view) return false;
  return !(Object.values(COVERED_OUTER_BOXES) as LonLatBox[]).some((box) => boxesOverlap(view, box));
}

/**
 * The honest coverage state for the current view. The unknown-≠-zero core:
 *  - `no-feed`       the view is confidently OUTSIDE coverage → absence of FEED, not zero
 *  - `live-none`     a confidently-covered view with a KNOWN zero → genuine "none right now"
 *  - `live-closures` closures are present in view (feed reaching here) → the map shows them
 *  - `unknown`       no view yet, an ambiguous border/multi-province view, or covered-but-
 *                    count-not-yet-known → assert nothing affirmative (standing note only)
 *
 * The ordering guarantees BOTH #337 HARD ❌s are impossible: closures-present wins first
 * (never "no feed" over a drawn closure), the affirmative "feed is live" reading requires
 * confident-covered (never over an uncovered province), and "no feed" requires
 * confident-uncovered (never over a covered province). Everything in between is 'unknown'.
 */
export type RoadClosureCoverageState = 'no-feed' | 'live-none' | 'live-closures' | 'unknown';

export function roadClosureCoverageState(
  view: LonLatBox | null | undefined,
  closuresInView: number | null | undefined,
): RoadClosureCoverageState {
  if (!view) return 'unknown';
  // Data present ⇒ the feed reaches here ⇒ NEVER label this "no feed" (the hard guarantee
  // against masking a visible closure).
  if (typeof closuresInView === 'number' && closuresInView > 0) return 'live-closures';
  // Confidently inside a covered province: a KNOWN zero is a genuine "none right now";
  // an unknown count (layer just turned on / mid-fetch) asserts nothing.
  if (viewConfidentlyCovered(view)) return closuresInView === 0 ? 'live-none' : 'unknown';
  // Confidently outside coverage: absence of FEED.
  if (viewConfidentlyUncovered(view)) return 'no-feed';
  // Ambiguous (near a covered border / spanning covered+uncovered) → standing note only.
  return 'unknown';
}
